// src/container/AboutUs/AboutUs.jsx
import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { images } from '../../constants';
import Wave from 'react-wavify';
import './AboutUs.css';

const AboutUs = () => {
    const [isVisible, setIsVisible] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        setIsVisible(true);
                    } else {
                        setIsVisible(false);
                    }
                });
            },
            { threshold: 0.1 }
        );

        if (ref.current) {
            observer.observe(ref.current);
        }

        return () => {
            if (ref.current) {
                observer.unobserve(ref.current);
            }
        };
    }, []);

    const slideInVariants = (direction, delay) => ({
        hidden: { opacity: 0, x: direction === 'left' ? -50 : 50 },
        visible: { opacity: 1, x: 0, transition: { duration: 0.5, delay, ease: 'easeOut' } }
    });

    const generateParticles = () => {
        const particles = [];
        for (let i = 0; i < 50; i++) { // Reduced number of particles
            const size = Math.random() * 5 + 2;
            const style = {
                width: `${size}px`,
                height: `${size}px`,
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                animationDuration: `${Math.random() * 3 + 2}s`, // Reduced animation duration
                animationDelay: `${Math.random() * 3}s` // Reduced animation delay
            };
            particles.push(<div key={i} className="particle" style={style}></div>);
        }
        return particles;
    };

    return (
        <div className="app__aboutus-container">
            {generateParticles()}
            <div className="app__aboutus flex__center section__padding" id="about">
                <motion.div
                    initial={{ opacity: 0, x: -100 }}
                    animate={
                        isVisible
                            ? {
                                opacity: 1,
                                x: 0,
                                transition: {
                                    duration: 0.8,
                                    type: 'spring',
                                    stiffness: 50,
                                    damping: 15
                                }
                            }
                            : {
                                opacity: 0,
                                x: 100,
                                transition: {
                                    duration: 0.5,
                                    type: 'spring',
                                    stiffness: 50,
                                    damping: 15
                                }
                            }
                    }
                    className="app__aboutus-content flex__center"
                    ref={ref}
                >
                    <div className="app__aboutus-content_history">
                        <h1 className="headtext__cormorant">À propos</h1>
                        <img src={images.chopstickSide}
                             style={{filter: 'invert(0.5) sepia(1) saturate(5) hue-rotate(10deg)'}}
                             alt="about_chopstick" className="chopstick__img"/>

                        <motion.p
                            className="p__opensans2"
                            initial="hidden"
                            animate={isVisible ? "visible" : "hidden"}
                            variants={slideInVariants('left', 0)}
                        >
                            Zoom au plein cœur du 8ème arrondissement de Paris, à proximité de l'Élysée et du Jardin
                            des Tuileries,
                            Au Mirodélice est un modeste restaurant en comparaison de ces lieux
                            emblématiques.
                            Cependant, nous vous offrons un moment de repos, où dans notre cadre intime, nous sommes dédiés à
                            vous apporter une touche d'exotisme au cœur de
                            la capitale.
                        </motion.p>
                        <img src={images.fryingPan}
                             alt="about_chopstick" className="size-16"/>
                        <motion.p
                            className="p__opensans2"
                            initial="hidden"
                            animate={isVisible ? "visible" : "hidden"}
                            variants={slideInVariants('right', 0.2)}
                        >
                            Escapade culinaire à travers les saveurs de l’Asie
                            sans quitter Paris. Chez Au Mirodélice, nous puisons notre inspiration dans les cuisines
                            traditionnelles asiatiques.
                            Notre chef, fort d’un riche héritage culinaire, s’inspire des cuisines cambodgiennes,
                            chinoises,
                            vietnamiennes… Pour créer des recettes authentiques où la fraîcheur des ingrédients se marie
                            avec
                            l’art subtil des épices.
                        </motion.p>
                        <img src={images.lanternGif}
                             alt="about_chopstick" className="size-14"/>
                        <motion.p
                            className="p__opensans2"
                            initial="hidden"
                            animate={isVisible ? "visible" : "hidden"}
                            variants={slideInVariants('left', 0.4)}
                        >
                            Nous avons conçu un menu varié et riche pour satisfaire toutes
                            vos envies culinaire, que vous soyez
                            friand de sucré-salé, ou en quête de saveurs plus douces. Que ce soit pour un
                            déjeuner rapide, un dîner entre amis, ou un moment de détente en famille,
                            nous nous engageons à vous offrir une expérience plaisante.
                        </motion.p>

                        <motion.p
                            className="p__opensans"
                            initial="hidden"
                            animate={isVisible ? "visible" : "hidden"}
                            variants={slideInVariants('right', 0.6)}
                        >
                            Venez profiter d'un instant de gourmandise. – <strong>L'équipe Mirodélice.</strong>
                        </motion.p>
                    </div>
                </motion.div>
            </div>
            <Wave fill='#FADB70'
                  paused={false}
                  style={{display: 'flex' }}
                  options={{
                      height: 20,
                      amplitude: 50,
                      speed: 0.15,
                      points: 3
                  }}
            />
        </div>
    );
};

export default AboutUs;